import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FormField, Heading, Icon, Label, List, Modal, Paragraph, Select } from '@zillow/constellation-10';
import { IconYardSignFilled } from '@zillow/constellation-icons';
import { useRentalManagerUserSingleFamilyEnterprisePropertyWebApiHandler_customerId } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import PayAndPublishCSEvents from '../../../model/ClickstreamEvent/PayAndPublishCSEvents';
import { trackGtmEvent } from '../../../../client/analytics';
import { ZRM_GTM_EVENTS } from '../../../../client/analytics/gtmEvents';

import numberUtils from '../../../utils/numberUtils';
import emailUtils from '../../../utils/emailUtils';

import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';

import ModalFooter from '../ModalFooter';

const propTypes = {
  contracts: PropTypes.arrayOf(
    PropTypes.shape({
      customerId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      unitOfMeasure: PropTypes.string.isRequired,
      productType: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
const defaultProps = {};

const PreActivationModal = ({ contracts, onClose, onSubmit }) => {
  const { mutateAsync: setCustomerIdMutation } =
    useRentalManagerUserSingleFamilyEnterprisePropertyWebApiHandler_customerId();

  const {
    properties: {
      currentProperty: { singleFamilyContract, listingAlias },
    },
    user: { isEnterpriseCustomer },
  } = useSelector((state) => state);

  const mustSetActiveContract = singleFamilyContract.isRequired && !singleFamilyContract.isActive;
  const defaultCustomerId = mustSetActiveContract && contracts.length === 1 ? contracts[0].customerId : null;
  const contract = singleFamilyContract?.isActive ? singleFamilyContract?.details : contracts[0];

  const [customerId, setCustomerId] = useState(defaultCustomerId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async () => {
    if (mustSetActiveContract && customerId === null) {
      const newErrorMessage =
        contracts.length === 0
          ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT
          : 'Please select an active contract';
      setErrorMessage(newErrorMessage);
    } else {
      setErrorMessage('');
      const setNewActiveContractPromise = mustSetActiveContract
        ? setCustomerIdMutation({ getParams: { listingAlias, customerId } })
        : Promise.resolve();
      try {
        if (mustSetActiveContract) {
          setIsSubmitting(true);
        }
        await setNewActiveContractPromise;
      } catch {
        const errMessage = `Unable to publish this property due to lack of an active contract.
                    Please contact ${emailUtils.getHelpEmail(false, true)}.`;
        setErrorMessage(errMessage);
        setIsSubmitting(false);
        return;
      }

      trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });
      setIsSubmitting(false);
      onClose();
      onSubmit();
    }
  };

  const infoList = [];

  if (!contract || mustSetActiveContract) {
    switch (true) {
      case isEnterpriseCustomer:
        infoList.push('This listing will be included in your monthly invoice.');
        break;
      default:
        break;
    }
  } else if (contract) {
    const formattedPrice = numberUtils.asCurrencyWithDefault(contract.unitCost, '-');

    switch (contract.productType) {
      case AdproductsConstants.ProductCodes.FLAT_FEE_FEED_CONNECT:
        infoList.push('This listing can be posted for free due to your monthly Zillow flat fee subscription');
        break;
      case AdproductsConstants.ProductCodes.SINGLE_FAMILY:
        infoList.push(
          `This listing will be billed at your monthly rate (${formattedPrice}) and included in your monthly invoice`,
        );
        break;
      case AdproductsConstants.ProductCodes.SINGLE_FAMILY_DAILY_LISTINGS:
        infoList.push(
          `This listing will be billed at your daily rate (${formattedPrice}) and included in your monthly invoice`,
        );
        break;
      default:
        break;
    }
  }

  infoList.push('Renters will see the Zillow Rental Manager version of the listing on Zillow, Trulia & HotPads');

  if (singleFamilyContract.isRequired) {
    infoList.push('You can cancel anytime by deactivating your listing from the listing page');
  }

  return (
    <Modal
      header={<Heading level={2}>Publish this listing?</Heading>}
      body={
        <>
          <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconYardSignFilled />} />
          <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>
            Things you need to know before publishing:
          </Paragraph>
          <List.Root className="PreActivationModal-List">
            {infoList.map((item, index) => (
              <List.Item key={index}>{item}</List.Item>
            ))}
          </List.Root>
          {mustSetActiveContract && contracts.length > 1 && (
            <FormField css={{ marginBlock: '400' }}>
              <Label>Active Contracts</Label>
              <Select
                aria-label="Active contracts"
                value={customerId || 'Please select'}
                onChange={(e) => setCustomerId(e.target.value)}
                css={{ whiteSpace: 'pre' }}
              >
                <option disabled={true}>Please select</option>
                {contracts.map((contract) => (
                  <option key={contract.customerId} value={contract.customerId}>
                    {contract.name}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </>
      }
      footer={
        <ModalFooter
          submitText="Yes, publish"
          isSubmitDisabled={mustSetActiveContract && customerId === null}
          errorMessage={errorMessage}
          hasError={!!errorMessage}
          onSubmit={handleSubmit}
          onSubmitDataEventId={PayAndPublishCSEvents.ActivateListing.data_event_id}
          onCancel={onClose}
          isSubmitting={isSubmitting}
        />
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

PreActivationModal.propTypes = propTypes;
PreActivationModal.defaultProps = defaultProps;

export { PreActivationModal };
export default PreActivationModal;
